import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import type { TokenPairResponse } from "../api-models";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./auth";

// The API is deployed as its own service, so its origin is a build-time setting.
// VITE_API_URL is either an absolute origin ("https://api.glut.example.com") or a path
// prefix; the default "/api" covers local dev (the Vite proxy in vite.config.ts) and the
// single-service deployment where the Go binary serves the built frontend itself.
export const API_URL = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

export const api = axios.create({ baseURL: API_URL });

// Same base, no interceptors: the endpoints that mint tokens must not be retried through
// the refresh flow below (a failing refresh would recurse) and have no token to attach.
export const authApi = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

function bearerOf(header: unknown): string | null {
  return typeof header === "string" && header.startsWith("Bearer ") ? header.slice(7) : null;
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");
  const res = await authApi.post<TokenPairResponse>("/auth/refresh", { refreshToken });
  setTokens(res.data.accessToken, res.data.refreshToken);
  return res.data.accessToken;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retried && !original.url?.includes("/auth/")) {
      original._retried = true;
      try {
        // Another tab (or an earlier request in this one) may already have renewed the
        // token while this request was in flight. Refresh tokens rotate, so refreshing
        // again with the value this tab read before that happened presents an
        // already-rotated token, which the API reads as a stolen session. If the stored
        // access token has moved on since this request went out, just retry with it.
        const current = getAccessToken();
        if (current && current !== bearerOf(original.headers?.Authorization)) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${current}`;
          return api.request(original);
        }
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const token = await refreshPromise;
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${token}`;
        return api.request(original);
      } catch (refreshErr) {
        // Only a server that actually rejected the refresh (or a missing refresh token)
        // means the session is over. A refresh that never reached the API — dev server
        // restarting, laptop offline — says nothing about it, and clearing tokens there
        // turns a two-second outage into a permanent logout.
        if (!getRefreshToken() || isSessionRejected(refreshErr)) {
          clearTokens();
          window.location.assign("/login");
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

// isSessionRejected separates "the API told us this session is no longer valid" from every
// other way a request can fail. Callers use it to decide whether to send the user back to
// the login page: a 4xx is the server's verdict, while a transport error or a 5xx is the
// API being unreachable and must not be read as a logout.
export function isSessionRejected(err: unknown): boolean {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
}

export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
